import { Subscription } from 'rxjs';
import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { cloneDeep } from 'lodash';

import { BriefingsService } from '../../services/briefings.service';
import { BriefingModel } from '../../models/briefing.model';
import { TextStyleFieldModel } from '../../../../Common/PageBuilder/models/text-style-field.model';
import { BriefingDateStyleFieldModel } from '../../models/briefing-date-style-field.model';
import { MentionCustomizationFieldModel } from '../../../../Monitor/Shared/models/mention-customization-field.model';
import { BriefingStyleStateEnum } from '../../../../Common/MonitorShared/enum/briefing-style-state.enum';
import { BriefingBlockTypeEnum } from '../../enums/briefing-block-type.enum';
import { briefingBlockTypeToGeneralStylesPropertyMap } from '../../models/briefing-global-styles-property.const';
import { GenericStyleModel } from '../../../../Common/Styles/models/generic-style.model';
import { StyleTypesApiEnum } from '../../../../api/styles/enum/style-types-api.enum';
import { TextStyleDefaultsModel } from '../../../../Common/PageBuilder/models/text-style-defaults.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { ImageStyleFieldModel } from '../../../../Common/PageBuilder/models/image-style-field.model';
import { ImageStyleFieldComponent } from '../../../../Common/PageBuilder/components/FormFields/ImageStyleField/image-style-field.component';
import { briefingsStyleDropdownOptions, canvasStyleDropdownOptions } from '../../models/briefings-style-dropdown-options.model';
import { LayoutStyleField } from '../../../../Common/PageBuilder/models/layout-style-field.model';
import {
    briefingOneColumnLayoutBlockInnerStyleField,
    briefingThreeColumnLayoutBlockInnerStyleField,
    briefingTwoColumnLayoutBlockInnerStyleField
} from '../../models/briefings-block-types.model';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { DividerStyleFieldModel } from '../../../../Common/PageBuilder/models/divider-style-field.model';
import { DividerStyleFieldComponent } from '../../../../Common/PageBuilder/components/FormFields/DividerField/divider-style-field.component';
import { DividerStyleDefaultsModel } from '../../../../Common/PageBuilder/models/divider-style-defaults.model';
import { PaddingModel } from '../../../../Common/PageBuilder/models/padding.model';
import { LinksStyleFieldModel } from '../../../../Common/PageBuilder/models/links-style-field.model';
import { LinksStyleFieldComponent } from '../../../../Common/PageBuilder/components/FormFields/LinksStyleField/links-style-field.component';
import { MonitoringTitleFieldModel } from '@Monitor/Shared/models/monitoring-title-field.model';
import { BriefingDeliveryMethodEnum } from '../../enums/briefing-delivery-method.enum';
import { CuratorStyleFieldModel } from '../../../../Common/PageBuilder/models/curator-style-field.model';
import { CuratorStyleFieldComponent } from '../../../../Common/PageBuilder/components/FormFields/CuratorStyleField/curator-style-field.component';
import { LayoutBlock } from '../../../../Common/PageBuilder/models/layout-block.model';
import { CuratorBlock } from '@Common/PageBuilder/models/curator-block.model';
import { MentionCustomizationModel } from '@Monitor/Shared/models/mention-customization.model';
import { BriefingImageDisplayElementModel } from '@Common/PageBuilder/models/briefing-image-display-element.model';
import { BriefingTitleStyleFieldComponent } from '@Monitor/Shared/components/BriefingTitleStyleField/briefing-title-style-field.component';
import { BriefingDateStyleFieldComponent } from '@Monitor/Shared/components/BriefingDateStyleField/briefing-date-style-field.component';
import { TextStyleFieldComponent } from '@Common/PageBuilder/components/TextStyleField/text-style-field.component';
import { StyleStateEnum } from '@Common/PageBuilder/enums/style-state.enum';
import { BriefingTextStyleFieldComponent } from '@Monitor/Shared/components/BriefingTextStyleField/briefing-text-style-field.component';
import { MentionCustomizationFieldComponent } from '@Monitor/Shared/components/MentionCustomizationField/mention-customization-field.component';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';

@Component({
    selector: 'agpr-briefing-global-customization',
    templateUrl: './briefing-global-customization.component.html',
    styleUrls: ['./briefing-global-customization.component.scss'],
    standalone: false
})
export class BriefingGlobalCustomizationComponent implements OnInit, OnDestroy {
    @Input() public id: string;
    @Output() public change: EventEmitter<void> = new EventEmitter<void>();

    currentBriefing: BriefingModel = new BriefingModel();
    subscriptions: Subscription[] = [];

    briefingBlockTypeEnum = BriefingBlockTypeEnum;

    imageField: TextStyleFieldModel;
    titleField: MonitoringTitleFieldModel;
    textField: TextStyleFieldModel;
    analysisField: TextStyleFieldModel;
    dateStyleField: BriefingDateStyleFieldModel;
    linkField: LinksStyleFieldModel;
    tableOfContentField: TextStyleFieldModel;
    dividerField: DividerStyleFieldModel;
    mentionStyleField: MentionCustomizationFieldModel;
    oneColumnLayoutField: LayoutStyleField;
    twoColumnLayoutField: LayoutStyleField;
    threeColumnLayoutField: LayoutStyleField;
    chartField: ImageStyleFieldModel;
    curatorField: CuratorStyleFieldModel;
    oldAccentColor: string;

    hasCoverageInsightsEnabled: boolean = false;
    isCanvas: boolean = false;

    readonly buttonTypes = ButtonTypesEnum;

    constructor(
        // This is actually briefingService, but injecting briefing service causes a circular dependency
        private briefingService: BriefingsService,
        private monitorConfigService: MonitorConfigService,
    ) {}

    ngOnInit() {
        this.subscriptions.push(
            this.briefingService.getFocusedResource().subscribe(
                currentBriefing => {
                    if (currentBriefing) {
                        this.currentBriefing = currentBriefing;
                        this.setupFields();
                        if (currentBriefing?.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                            this.isCanvas = true;
                            this.imageField.allowExpertMode = false;
                            this.titleField.allowExpertMode = false;
                            this.textField.allowExpertMode = false;
                            this.analysisField.allowExpertMode = false;
                            this.dateStyleField.allowExpertMode = false;
                            this.linkField.allowExpertMode = false;
                            this.tableOfContentField.allowExpertMode = false;
                            this.dividerField.allowExpertMode = false;
                            this.mentionStyleField.allowExpertMode = false;
                            this.oneColumnLayoutField.allowExpertMode = false;
                            this.twoColumnLayoutField.allowExpertMode = false;
                            this.threeColumnLayoutField.allowExpertMode = false;
                            this.chartField.allowExpertMode = false;
                            this.curatorField.allowExpertMode = false;
                            this.oldAccentColor = this.currentBriefing.generalStyles.accentColor;
                        } else {
                            this.isCanvas = false;
                            this.imageField.allowExpertMode = true;
                            this.titleField.allowExpertMode = true;
                            this.textField.allowExpertMode = true;
                            this.analysisField.allowExpertMode = true;
                            this.dateStyleField.allowExpertMode = true;
                            this.linkField.allowExpertMode = true;
                            this.tableOfContentField.allowExpertMode = true;
                            this.dividerField.allowExpertMode = true;
                            this.mentionStyleField.allowExpertMode = true;
                            this.oneColumnLayoutField.allowExpertMode = true;
                            this.twoColumnLayoutField.allowExpertMode = true;
                            this.threeColumnLayoutField.allowExpertMode = true;
                            this.chartField.allowExpertMode = true;
                            this.curatorField.allowExpertMode = true;
                        }
                    }
                }
            ),
            this.monitorConfigService.getHasCoverageInsightsEnabled().subscribe(x => this.hasCoverageInsightsEnabled = x)
        );
    }

    setupFields(): void {
        this.imageField = new ImageStyleFieldModel(
            'image',
            true,
            new TextStyleDefaultsModel(
                '24px',
                '#5e6473',
                true
            ),
            'Briefing.imageTextStyle.image.label',
            true,
            true,
            true,
            StyleTypesApiEnum.IMAGE,
            ImageStyleFieldComponent,
            '',
            briefingsStyleDropdownOptions,
            (x: ImageStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return canvasStyleDropdownOptions;
                } else {
                    return briefingsStyleDropdownOptions;
                }
            },
            false
        );

        this.titleField = new MonitoringTitleFieldModel(
            'title',
            false,
            new TextStyleDefaultsModel(
                '24px',
                '#5e6473',
                true,
            ),
            'Briefing.textStyle.title.label',
            true,
            true,
            true,
            StyleTypesApiEnum.TITLE,
            null,
            null,
            true,
            true,
            briefingsStyleDropdownOptions,
            (x: BriefingTitleStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return canvasStyleDropdownOptions;
                } else {
                    return briefingsStyleDropdownOptions;
                }
            }
        );

        this.textField = new TextStyleFieldModel(
            'title',
            true,
            new TextStyleDefaultsModel(
                '14px',
                '#292929',
                false
            ),
            'Briefing.textStyle.text.label',
            true,
            true,
            true,
            StyleTypesApiEnum.TEXT,
            null,
            null,
            true,
            true,
            briefingsStyleDropdownOptions,
            (x: TextStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return canvasStyleDropdownOptions;
                } else {
                    return briefingsStyleDropdownOptions;
                }
            }
        );

        this.dateStyleField = new BriefingDateStyleFieldModel(
            null,
            'date',
            'DateStyleField.dateLine',
            '',
            (x: BriefingDateStyleFieldComponent) => {
                const deliveryMethod = (x.briefingsService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return canvasStyleDropdownOptions;
                } else {
                    return briefingsStyleDropdownOptions;
                }
            }
        );

        this.linkField = new LinksStyleFieldModel(
            'styles',
            LinksStyleFieldComponent,
            'PageBuilder.formField.linkLabel',
            null,
            [
                { label: 'Briefings.formField.styles.defaultAgilityStyleOne', value: BriefingStyleStateEnum.BRIEFING_1 },
                { label: 'Briefings.formField.styles.custom', value: BriefingStyleStateEnum.CUSTOM }
            ],
            StyleTypesApiEnum.LINK,
            (x: LinksStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return [
                        { label: 'Briefings.formField.styles.defaultAgilityCanvasOne', value: BriefingStyleStateEnum.CANVAS_1 },
                        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
                    ];
                } else {
                    return [
                        { label: 'PageBuilder.formField.styles.defaultAgilityStyle', value: StyleStateEnum.DEFAULT },
                        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
                    ];
                }
            }
        );

        this.tableOfContentField = new TextStyleFieldModel(
            'tableOfContent',
            true,
            new TextStyleDefaultsModel(
                '14px',
                '#5e6473',
                true
            ),
            'Briefings.blockType.tableOfContent',
            true,
            true,
            true,
            StyleTypesApiEnum.TABLE_OF_CONTENT,
            null,
            null,
            true,
            true,
            briefingsStyleDropdownOptions,
            (x: BriefingTextStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return canvasStyleDropdownOptions;
                } else {
                    return briefingsStyleDropdownOptions;
                }
            }
        );

        this.dividerField = new DividerStyleFieldModel(
            'styles',
            true,
            new DividerStyleDefaultsModel(
                2,
                '#C7C7C7',
                '#FFFFFF',
                new PaddingModel(0, true, 20, 0, 20 , 0)
            ),
            'PageBuilder.formField.dividerLabel',
            true,
            StyleTypesApiEnum.DIVIDER,
            DividerStyleFieldComponent,
            undefined,
            briefingsStyleDropdownOptions,
            (x: DividerStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return [
                        { label: 'Briefings.formField.styles.defaultAgilityCanvasOne', value: BriefingStyleStateEnum.CANVAS_1 },
                        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
                    ];
                } else {
                    return [
                        { label: 'PageBuilder.formField.styles.defaultAgilityStyle', value: StyleStateEnum.DEFAULT },
                        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
                    ];
                }
            }
        );

        this.mentionStyleField = new MentionCustomizationFieldModel(
            null,
            'mention',
            'MentionStyleField.heading',
            '',
            undefined,
            (x: MentionCustomizationFieldComponent) => {
                const deliveryMethod = (x.briefingsService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    const options: DropdownOptionModel[] = cloneDeep(canvasStyleDropdownOptions);
                    const canvasOne: DropdownOptionModel = options.find(({value}) => value === BriefingStyleStateEnum.CANVAS_1);
                    if (canvasOne) {
                        canvasOne.label = 'Briefings.formField.styles.defaultAgilityStyleGrid';
                    }
                    return options;
                } else {
                    return briefingsStyleDropdownOptions;
                }
            }
        );

        this.oneColumnLayoutField = briefingOneColumnLayoutBlockInnerStyleField;
        this.twoColumnLayoutField = briefingTwoColumnLayoutBlockInnerStyleField;
        this.threeColumnLayoutField = briefingThreeColumnLayoutBlockInnerStyleField;

        this.analysisField = new TextStyleFieldModel(
            'analysis',
            true,
            new TextStyleDefaultsModel(
                '14px',
                '#292929',
                false
            ),
            'Briefing.textStyle.analysis.label',
            true,
            true,
            true,
            StyleTypesApiEnum.ANALYSIS,
            null,
            null,
            true,
            true,
            briefingsStyleDropdownOptions,
            (x: BriefingTextStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return canvasStyleDropdownOptions;
                } else {
                    return briefingsStyleDropdownOptions;
                }
            }
        );

        this.chartField = new ImageStyleFieldModel(
            'chart',
            true,
            new TextStyleDefaultsModel(
                '24px',
                '#5e6473',
                true
            ),
            'Briefings.blockType.chart',
            true,
            true,
            true,
            StyleTypesApiEnum.CHART,
            ImageStyleFieldComponent,
            '',
            briefingsStyleDropdownOptions,
            (x: ImageStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return canvasStyleDropdownOptions;
                } else {
                    return briefingsStyleDropdownOptions;
                }
            },
            true,
        );

        this.curatorField = new CuratorStyleFieldModel(
            CuratorStyleFieldComponent,
            (x: CuratorStyleFieldComponent) => {
                const deliveryMethod = (x.abstractPageBuilderService as BriefingsService).getCurrentBriefingDeliveryMethod();
                if (deliveryMethod === BriefingDeliveryMethodEnum.ONLINE) {
                    return [
                        { label: 'Briefings.formField.styles.defaultAgilityCanvasOne', value: BriefingStyleStateEnum.CANVAS_1 },
                        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
                    ];
                } else {
                    return [
                        { label: 'PageBuilder.formField.styles.defaultAgilityStyle', value: StyleStateEnum.DEFAULT },
                        { label: 'PageBuilder.formField.styles.custom', value: StyleStateEnum.CUSTOM }
                    ];
                }
            }
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    /**
     * Handles changes to date style
     */
    styleChange(styleModel: GenericStyleModel, blockType: BriefingBlockTypeEnum): void {
        const briefingGeneralStylesProperty = briefingBlockTypeToGeneralStylesPropertyMap.get(blockType);

        if (!styleModel.isSavedStyle() && (styleModel as any).displayStyleState !== BriefingStyleStateEnum.CUSTOM) {
            const defaultStyle = this.briefingService.getDefaultStyle(blockType, styleModel.styleState as BriefingStyleStateEnum);
            defaultStyle.template = null;
            this.currentBriefing.generalStyles.setProperty(briefingGeneralStylesProperty, defaultStyle.clone());
            this.briefingService.generalStylesBlockUpdated(blockType, defaultStyle);

            return;
        }

        this.currentBriefing.generalStyles.setProperty(briefingGeneralStylesProperty, styleModel.clone());
        this.briefingService.generalStylesBlockUpdated(blockType, styleModel);
    }

    accentColorChanged(): void {
        this.briefingService.setGeneralStyles(this.currentBriefing.generalStyles);
        this.briefingService.setCurrentBriefing(this.currentBriefing);
        const accentColor = this.currentBriefing.generalStyles.accentColor;
        this.currentBriefing.generalStyles.mention.heading.color = accentColor;
        const styleAffectedBlocks = [];
        const imageBlocks = [];
        const mentionBlockTypes = [
            BriefingBlockTypeEnum.CURATED_MENTION_LIST,
            BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST
        ];
        this.currentBriefing.blocks.forEach(block => {
            if ((mentionBlockTypes.includes(block.type.key as BriefingBlockTypeEnum) &&
                (block.displayElement.styles as MentionCustomizationModel).heading.color === this.oldAccentColor) ||
                block.type.key === BriefingBlockTypeEnum.CHART) {
                styleAffectedBlocks.push(block);
            } else if (block.type.key === BriefingBlockTypeEnum.IMAGE &&
                (block.displayElement as BriefingImageDisplayElementModel).imageEffects.primaryColor === this.oldAccentColor) {
                imageBlocks.push(block);
            } else if (block.isLayoutBlockType()) {
                (block as LayoutBlock).blocks.forEach(column => column.forEach(innerBlock => {
                    if ((mentionBlockTypes.includes(innerBlock.type.key as BriefingBlockTypeEnum) &&
                        (innerBlock.displayElement.styles as MentionCustomizationModel).heading.color === this.oldAccentColor) ||
                        innerBlock.type.key === BriefingBlockTypeEnum.CHART) {
                        styleAffectedBlocks.push(innerBlock);
                    }
                    if (innerBlock.type.key === BriefingBlockTypeEnum.IMAGE &&
                        (innerBlock.displayElement as BriefingImageDisplayElementModel).imageEffects.primaryColor === this.oldAccentColor) {
                        imageBlocks.push(innerBlock);
                    }
                }));
            } else if (block.type.key === BriefingBlockTypeEnum.COVERAGE_CURATOR) {
                (block as CuratorBlock).blocks.forEach(innerBlock => {
                    if (((innerBlock.type.key as BriefingBlockTypeEnum) === BriefingBlockTypeEnum.CURATED_MENTION_LIST
                        || (innerBlock.type.key as BriefingBlockTypeEnum) === BriefingBlockTypeEnum.AUTOMATIC_MENTION_LIST)
                        && (innerBlock.displayElement.styles as MentionCustomizationModel).heading.color === this.oldAccentColor) {
                        styleAffectedBlocks.push(innerBlock);
                    }
                    if (innerBlock.type.key === BriefingBlockTypeEnum.IMAGE &&
                        (innerBlock.displayElement as BriefingImageDisplayElementModel).imageEffects.primaryColor === this.oldAccentColor) {
                        imageBlocks.push(innerBlock);
                    }
                });
            }
        });
        styleAffectedBlocks.forEach(block => this.briefingService.applyGeneralStylesToBlock(block));
        imageBlocks.forEach(block => {
            (block.displayElement as BriefingImageDisplayElementModel).imageEffects.primaryColor = accentColor;
            // HACK: re-assign to trigger change detection
            (block.displayElement as BriefingImageDisplayElementModel).imageEffects =
                cloneDeep((block.displayElement as BriefingImageDisplayElementModel).imageEffects);
        });

        this.oldAccentColor = accentColor;
        this.change.emit();
    }
}
